import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Calendar, Phone, MapPin, Share2, Home } from 'lucide-react';

const Success = () => {
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    // Retrieve booking data from localStorage
    const savedBooking = localStorage.getItem('lastBooking');
    if (savedBooking) {
      setBooking(JSON.parse(savedBooking));
    } else {
      // If no booking found, redirect to home
      navigate('/');
    }
  }, [navigate]);

  const handleAddToCalendar = () => {
    // Simple implementation - in production, use proper calendar API
    alert('Calendar integration would be implemented here');
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Cleansing Therapy Booking',
        text: 'I just booked a Cleansing Therapy session!',
        url: window.location.origin,
      });
    } else {
      alert('Sharing not supported on this device');
    }
  };

  if (!booking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-accent to-green-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const timeSlotMap = {
    morning: '8:00 AM - 12:00 PM',
    afternoon: '12:00 PM - 4:00 PM',
    evening: '4:00 PM - 8:00 PM',
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-accent to-green-100 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Success Animation */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{
            type: 'spring',
            stiffness: 260,
            damping: 20,
            delay: 0.2,
          }}
          className="text-center mb-8"
        >
          <div className="inline-block relative">
            <CheckCircle className="w-24 h-24 text-primary" />
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 2, opacity: 0 }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                repeatDelay: 0.5,
              }}
              className="absolute inset-0 border-4 border-primary rounded-full"
            />
          </div>
        </motion.div>

        {/* Success Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white rounded-3xl p-8 shadow-xl mb-6"
        >
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 text-center">
            Your Cleansing Therapy Session is Confirmed! 🎉
          </h1>
          <p className="text-center text-gray-600 mb-8">
            Confirmation Number: <span className="font-bold text-primary">{booking.confirmationNumber}</span>
          </p>

          {/* Booking Details */}
          <div className="space-y-4 mb-8">
            <div className="flex items-start gap-4 p-4 bg-primary/5 rounded-xl">
              <Calendar className="w-6 h-6 text-primary mt-1" />
              <div>
                <p className="font-semibold text-gray-800">When</p>
                <p className="text-gray-600">
                  {new Date(booking.scheduledDate).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
                <p className="text-gray-600">{timeSlotMap[booking.preferredTime]}</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-primary/5 rounded-xl">
              <MapPin className="w-6 h-6 text-primary mt-1" />
              <div>
                <p className="font-semibold text-gray-800">Location</p>
                <p className="text-gray-600">{booking.location}</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-primary/5 rounded-xl">
              <Phone className="w-6 h-6 text-primary mt-1" />
              <div>
                <p className="font-semibold text-gray-800">Contact</p>
                <p className="text-gray-600">{booking.name}</p>
                <p className="text-gray-600">{booking.mobile}</p>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-accent/50 rounded-2xl p-6 mb-6">
            <h3 className="font-bold text-gray-800 mb-3">Before Your Session:</h3>
            <ul className="space-y-2 text-gray-700 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Avoid heavy meals 4 hours before the session</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Stay hydrated - drink plenty of water</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Wear comfortable, loose clothing</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Arrive 10 minutes early for consultation</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="grid md:grid-cols-2 gap-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleAddToCalendar}
              className="flex items-center justify-center gap-2 bg-primary text-white py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-300"
            >
              <Calendar className="w-5 h-5" />
              Add to Calendar
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleShare}
              className="flex items-center justify-center gap-2 bg-white border-2 border-primary text-primary py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-300"
            >
              <Share2 className="w-5 h-5" />
              Share
            </motion.button>
          </div>
        </motion.div>

        {/* Referral Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-gradient-to-r from-primary to-green-600 rounded-3xl p-8 shadow-xl text-white text-center mb-6"
        >
          <h3 className="text-2xl font-bold mb-2">Refer & Earn Detox Points!</h3>
          <p className="mb-4 opacity-90">
            Share the wellness with friends and earn rewards for your next session
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-white text-primary px-8 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-300"
          >
            Get Referral Link
          </motion.button>
        </motion.div>

        {/* Home Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-gray-700 hover:text-primary transition-colors font-medium"
          >
            <Home className="w-5 h-5" />
            Back to Home
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default Success;
